import { apiClient } from './api';
import { Product, SellerOffer } from '../types';

export interface ProductsResponse {
  products: Product[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface GetProductsParams {
  category?: string;
  search?: string;
  sellerId?: string;
  inStock?: boolean;
  page?: number;
  limit?: number;
  sortBy?: 'price' | 'name' | 'rating';
  order?: 'asc' | 'desc';
}

export interface CategoriesResponse {
  categories: string[];
}

type CatalogSortField = 'name' | 'price' | 'createdAt' | 'updatedAt' | 'inventoryQuantity' | 'relevance';

interface CatalogPageMetadata {
  number?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
}

interface CatalogPagedResult<T> {
  items?: T[];
  page?: CatalogPageMetadata;
}

interface CatalogProductSummary {
  id?: string;
  name?: string;
  slug?: string;
  shortDescription?: string;
  price?: number | string | null;
  listPrice?: number | string | null;
  discountAmount?: number | string | null;
  discountPercent?: number | string | null;
  shippingCharge?: number | string | null;
  returnFee?: number | string | null;
  restockingFee?: number | string | null;
  currency?: string | null;
  taxGroup?: string | null;
  status?: string | null;
  inventoryQuantity?: number | null;
  sellerId?: string | null;
  categoryId?: string | null;
  categoryName?: string | null;
  imageUrl?: string | null;
  tags?: string[] | null;
  attributes?: Record<string, unknown> | null;
  images?: string[] | null;
}

interface CatalogProductDetail {
  summary?: CatalogProductSummary | null;
  description?: string | null;
  backorderThreshold?: number | null;
  backorderLimit?: number | null;
  attributes?: Record<string, unknown> | null;
}

interface CatalogCategory {
  id?: string;
  code?: string;
  name?: string;
  description?: string | null;
}

interface RelatedOffer {
  id?: string;
  name?: string;
  slug?: string;
  price?: number | string | null;
  currency?: string | null;
  status?: string | null;
  sellerId?: string | null;
  imageUrl?: string | null;
   inventory?: number | null;
   condition?: string | null;
}

const PLACEHOLDER_IMAGE = 'https://via.placeholder.com/400x400?text=Product';

type ApiError = Error & { status?: number };
type ProductRecord = Record<string, unknown>;
interface LegacyProductsResponse {
  products: unknown[];
  pagination?: unknown;
}

const asProductRecord = (value: unknown): ProductRecord =>
  value && typeof value === 'object' ? (value as ProductRecord) : {};

const isAuthorizationError = (error: unknown): error is ApiError =>
  error instanceof Error && [401, 403].includes((error as ApiError).status ?? 0);

const fetchCatalogResource = async <T>(endpoint: string): Promise<T> => {
  try {
    return await apiClient.get<T>(endpoint);
  } catch (error) {
    if (isAuthorizationError(error)) {
      return apiClient.get<T>(endpoint, true);
    }
    throw error;
  }
};

const generateFallbackId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `offer-${Math.random().toString(36).slice(2, 11)}`;
};

const toNumber = (value: unknown, fallback = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const extractMetric = (attributes: Record<string, unknown> | null | undefined, key: string): number | undefined => {
  if (!attributes || typeof attributes !== 'object') {
    return undefined;
  }
  const value = (attributes as Record<string, unknown>)[key];
  if (value === undefined || value === null) {
    return undefined;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const getAttributeString = (attributes: Record<string, unknown> | undefined, ...keys: string[]): string | undefined => {
  if (!attributes) {
    return undefined;
  }
  for (const key of keys) {
    const value = attributes[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return undefined;
};

const getAttributeNumber = (attributes: Record<string, unknown> | undefined, ...keys: string[]): number | undefined => {
  if (!attributes) {
    return undefined;
  }
  for (const key of keys) {
    const value = attributes[key];
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === 'string') {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }
  return undefined;
};

const normalizeSummary = (summary: CatalogProductSummary | null | undefined): Product => {
  const attributes = summary?.attributes && typeof summary.attributes === 'object'
    ? summary.attributes
    : undefined;

  const ratingFromAttributes = extractMetric(attributes, 'rating') ?? 0;
  const reviewsFromAttributes = extractMetric(attributes, 'reviewCount') ?? 0;

  const inventoryQuantity = typeof summary?.inventoryQuantity === 'number'
    ? summary.inventoryQuantity
    : null;

  const status = summary?.status ?? undefined;
  const rawPrice = toNumber(summary?.price, 0);
  const sellerName = getAttributeString(attributes, 'sellerName', 'seller_name', 'sellerDisplayName', 'seller.displayName');
  const sellerRating = getAttributeNumber(attributes, 'sellerRating', 'seller_rating');
  const sellerLogoUrl = getAttributeString(attributes, 'sellerLogoUrl', 'seller_logo_url', 'seller.logoUrl');

  let sellerBadges: string[] | undefined;
  const badgesCandidate = attributes?.sellerBadges ?? attributes?.seller_badges ?? attributes?.['seller.badges'];
  if (Array.isArray(badgesCandidate)) {
    sellerBadges = badgesCandidate
      .map((badge) => {
        if (typeof badge === 'string') {
          return badge.trim();
        }
        if (badge && typeof badge === 'object' && 'name' in badge && typeof badge.name === 'string') {
          return badge.name.trim();
        }
        return null;
      })
      .filter((badge): badge is string => Boolean(badge && badge.length > 0));
  }

  const rawImages = Array.isArray(summary?.images)
    ? summary.images.filter((value): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];

  // Prefer backend-provided primary (imageUrl), then fall back to images[].
  const primaryImage = summary?.imageUrl || rawImages[0] || undefined;

  const images = (() => {
    if (!primaryImage && rawImages.length === 0) {
      return undefined;
    }
    const unique: string[] = [];
    const addIfNew = (url: string | null | undefined) => {
      if (typeof url === 'string') {
        const trimmed = url.trim();
        if (trimmed && !unique.includes(trimmed)) {
          unique.push(trimmed);
        }
      }
    };
    addIfNew(primaryImage);
    rawImages.forEach(addIfNew);
    return unique.length > 0 ? unique : undefined;
  })();

  return {
    id: summary?.id ? String(summary.id) : '',
    name: summary?.name ?? 'Untitled product',
    price: rawPrice,
    description: summary?.shortDescription ?? '',
    category: summary?.categoryName ?? 'General',
    image: primaryImage ?? PLACEHOLDER_IMAGE,
    inStock: (inventoryQuantity ?? 0) > 0 && status !== 'DISCONTINUED',
    rating: ratingFromAttributes,
    reviews: reviewsFromAttributes,
    currency: summary?.currency ?? undefined,
    status,
    sellerId: summary?.sellerId ?? undefined,
    sellerName,
    sellerRating,
    sellerLogoUrl,
    sellerBadges,
    slug: summary?.slug ?? undefined,
    shortDescription: summary?.shortDescription ?? undefined,
    listPrice: toNullableNumber(summary?.listPrice),
    discountAmount: toNullableNumber(summary?.discountAmount),
    discountPercent: toNullableNumber(summary?.discountPercent),
    shippingCharge: toNullableNumber(summary?.shippingCharge),
    returnFee: toNullableNumber(summary?.returnFee),
    restockingFee: toNullableNumber(summary?.restockingFee),
    inventoryQuantity,
    attributes,
    imageAltText: summary?.name ?? undefined,
    images
  };
};

const normalizeLegacyProduct = (legacy: unknown): Product => {
  const source = asProductRecord(legacy);
  const status = typeof source.status === 'string' ? source.status : undefined;
  const inventoryQuantity =
    typeof source.inventory === 'number'
      ? source.inventory
      : toNullableNumber(source.inventoryQuantity);

  const rawImages = Array.isArray(source.images)
    ? source.images.filter((value: unknown): value is string => typeof value === 'string' && value.trim().length > 0)
    : [];

  // Prefer backend primary (image / imageUrl), then fall back to images[].
  const primaryImage =
    (typeof source.image === 'string' ? source.image : undefined) ??
    (typeof source.imageUrl === 'string' ? source.imageUrl : undefined) ??
    rawImages[0] ??
    undefined;

  const images = (() => {
    if (!primaryImage && rawImages.length === 0) {
      return undefined;
    }
    const unique: string[] = [];
    const addIfNew = (url: string | null | undefined) => {
      if (typeof url === 'string') {
        const trimmed = url.trim();
        if (trimmed && !unique.includes(trimmed)) {
          unique.push(trimmed);
        }
      }
    };
    addIfNew(primaryImage);
    rawImages.forEach(addIfNew);
    return unique.length > 0 ? unique : undefined;
  })();

  return {
    id: typeof source.id === 'number' ? source.id.toString() : String(source.id ?? ''),
    name: typeof source.name === 'string' ? source.name : 'Untitled product',
    price: toNumber(source.price, 0),
    description: typeof source.description === 'string' ? source.description : '',
    category: typeof source.category === 'string' ? source.category : 'General',
    image: primaryImage ?? PLACEHOLDER_IMAGE,
    inStock: (inventoryQuantity ?? 0) > 0 && status !== 'DISCONTINUED',
    rating: typeof source.rating === 'number' ? source.rating : 0,
    reviews: typeof source.reviews === 'number' ? source.reviews : 0,
    currency: typeof source.currency === 'string' ? source.currency : undefined,
    status,
    sellerId: source.sellerId ? String(source.sellerId) : undefined,
    sellerName: typeof source.sellerName === 'string' ? source.sellerName : undefined,
    sellerRating: typeof source.sellerRating === 'number' ? source.sellerRating : undefined,
    slug: typeof source.slug === 'string' ? source.slug : undefined,
    shortDescription: typeof source.shortDescription === 'string' ? source.shortDescription : undefined,
    listPrice: toNullableNumber(source.listPrice),
    discountAmount: toNullableNumber(source.discountAmount),
    discountPercent: toNullableNumber(source.discountPercent),
    shippingCharge: toNullableNumber(source.shippingCharge),
    returnFee: toNullableNumber(source.returnFee),
    restockingFee: toNullableNumber(source.restockingFee),
    inventoryQuantity,
    attributes:
      source.attributes && typeof source.attributes === 'object' && !Array.isArray(source.attributes)
        ? (source.attributes as Record<string, unknown>)
        : undefined,
    imageAltText: typeof source.imageAltText === 'string' ? source.imageAltText : undefined,
    images
  };
};

const normalizePagination = (pagination: unknown, fallbackLimit = 20): ProductsResponse['pagination'] => {
  const source = asProductRecord(pagination);
  const pageNumber = Number(source.page ?? source.number ?? 0);
  const size = Number(source.limit ?? source.size ?? fallbackLimit);
  const total = Number(source.total ?? source.totalElements ?? 0);
  const totalPages = Number(source.totalPages ?? (size > 0 ? Math.ceil(total / size) : 1) ?? 1);

  return {
    page: pageNumber >= 1 ? pageNumber : pageNumber + 1,
    limit: size > 0 ? size : fallbackLimit,
    total: total >= 0 ? total : 0,
    totalPages: totalPages >= 1 ? totalPages : 1
  };
};

const normalizeCatalogResponse = (raw: CatalogPagedResult<CatalogProductSummary>): ProductsResponse => {
  const items = Array.isArray(raw.items) ? raw.items : [];
  const page = raw.page ?? {};
  const limit = Number(page.size ?? 20) || 20;

  const products = items.map(normalizeSummary);
  const pagination = {
    page: typeof page.number === 'number' ? page.number + 1 : 1,
    limit,
    total: typeof page.totalElements === 'number' ? page.totalElements : products.length,
    totalPages: typeof page.totalPages === 'number' ? page.totalPages : Math.max(1, Math.ceil(products.length / limit))
  };

  return { products, pagination };
};

const isCatalogPagedResult = (payload: unknown): payload is CatalogPagedResult<CatalogProductSummary> => {
  const source = asProductRecord(payload);
  return Array.isArray(source.items) && 'page' in source;
};

const isLegacyProductsResponse = (payload: unknown): payload is LegacyProductsResponse => {
  const source = asProductRecord(payload);
  return Array.isArray(source.products);
};

const resolveSortField = (sortBy?: GetProductsParams['sortBy']): CatalogSortField | null => {
  switch (sortBy) {
    case 'price':
      return 'price';
    case 'name':
      return 'name';
    case 'rating':
      return 'relevance';
    default:
      return null;
  }
};

const buildCatalogQuery = (params?: GetProductsParams): URLSearchParams => {
  const query = new URLSearchParams();

  const limit = params?.limit ?? 20;
  query.set('size', Math.max(1, limit).toString());

  if (params?.page !== undefined) {
    const zeroIndexedPage = Math.max(0, params.page - 1);
    query.set('page', zeroIndexedPage.toString());
  }

  if (params?.category) {
    query.append('category', params.category);
  }

  if (params?.search) {
    query.append('search', params.search);
  }

  if (params?.sellerId) {
    query.append('sellerId', params.sellerId);
  }

  if (params?.inStock !== undefined) {
    query.append('inStock', String(params.inStock));
  }

  const sortField = resolveSortField(params?.sortBy);
  if (sortField) {
    if (sortField === 'relevance') {
      query.append('sort', sortField);
    } else {
      const direction = params?.order === 'desc' ? 'desc' : 'asc';
      query.append('sort', `${sortField},${direction}`);
    }
  }

  return query;
};

const mapOfferToSeller = (offer: RelatedOffer): SellerOffer => {
  const sellerId = offer?.sellerId ? String(offer.sellerId) : undefined;
  const fallbackSellerName = sellerId ? `Seller ${sellerId.slice(0, 8)}` : 'Marketplace seller';

  return {
    sellerProductId: offer?.id ? String(offer.id) : generateFallbackId(),
    sellerId,
    sellerName: offer?.name ?? fallbackSellerName,
    sellerRating: undefined,
    price: toNumber(offer?.price, 0),
    stock: typeof offer?.inventory === 'number' ? offer.inventory : null,
    condition: offer?.condition ?? null,
    warrantyMonths: null,
    shippingDays: null,
    status: offer?.status ?? undefined,
    currency: offer?.currency ?? undefined,
    imageUrl: offer?.imageUrl ?? null,
    slug: offer?.slug ?? undefined,
    badges: undefined,
    logoUrl: null
  };
};

const mergeSpecifications = (raw: unknown): Record<string, unknown> => {
  if (!raw || typeof raw !== 'object') {
    return {};
  }

  const source = asProductRecord(raw);
  if (Array.isArray(source.items)) {
    return source.items.reduce<Record<string, unknown>>((accumulator, item) => {
      const itemSource = asProductRecord(item);
      if (itemSource.specifications && typeof itemSource.specifications === 'object' && !Array.isArray(itemSource.specifications)) {
        Object.entries(itemSource.specifications as Record<string, unknown>).forEach(([key, value]) => {
          if (!(key in accumulator)) {
            accumulator[key] = value;
          }
        });
      }
      return accumulator;
    }, {});
  }

  if ('specifications' in source) {
    const specifications = source.specifications;
    if (specifications && typeof specifications === 'object') {
      return specifications as Record<string, unknown>;
    }
  }

  if (!Array.isArray(raw) && typeof raw === 'object') {
    return raw as Record<string, unknown>;
  }

  return {};
};

const normalizeCategories = (raw: unknown): CategoriesResponse => {
  if (!Array.isArray(raw)) {
    return { categories: [] };
  }

  const categories = raw
    .map((item: unknown) => {
      if (typeof item === 'string') {
        return item;
      }
      if (item && typeof item === 'object' && 'name' in item) {
        const candidate = (item as CatalogCategory).name;
        return candidate ?? null;
      }
      return null;
    })
    .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

  return { categories };
};

export const productsApi = {
  /**
   * Get all products with optional filtering and pagination
   */
  getProducts: async (params?: GetProductsParams): Promise<ProductsResponse> => {
    const query = buildCatalogQuery(params);
    const endpoint = query.toString()
      ? `/v2/products?${query.toString()}`
      : '/v2/products';

    const raw = await fetchCatalogResource<unknown>(endpoint);

    if (isCatalogPagedResult(raw)) {
      return normalizeCatalogResponse(raw);
    }

    if (isLegacyProductsResponse(raw)) {
      return {
        products: raw.products.map(normalizeLegacyProduct),
        pagination: normalizePagination(raw.pagination ?? {}, params?.limit ?? 20)
      };
    }

    if (Array.isArray(raw)) {
      return {
        products: raw.map(normalizeLegacyProduct),
        pagination: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 20,
          total: raw.length,
          totalPages: 1
        }
      };
    }

    return {
      products: [],
      pagination: {
        page: params?.page ?? 1,
        limit: params?.limit ?? 20,
        total: 0,
        totalPages: 1
      }
    };
  },

  /**
   * Get product by ID
   */
  getProductById: async (id: string): Promise<Product> => {
    const endpoint = `/v2/products/${encodeURIComponent(id)}`;
    const raw = await fetchCatalogResource<unknown>(endpoint);

    if (raw && typeof raw === 'object' && 'summary' in raw) {
      const detail = raw as CatalogProductDetail;
      const summaryProduct = normalizeSummary(detail.summary ?? null);
      return {
        ...summaryProduct,
        description: detail.description ?? summaryProduct.description,
        attributes: detail.attributes ?? summaryProduct.attributes
      };
    }

    if (raw && typeof raw === 'object') {
      return normalizeLegacyProduct(raw);
    }

    return {
      id: String(id),
      name: 'Unknown product',
      price: 0,
      description: '',
      category: 'General',
      image: PLACEHOLDER_IMAGE,
      inStock: false,
      rating: 0,
      reviews: 0
    };
  },

  /**
   * Get all product categories
   */
  getCategories: async (): Promise<CategoriesResponse> => {
    const raw = await fetchCatalogResource<unknown>('/v2/categories');
    return normalizeCategories(raw);
  },

  /**
   * Get all seller offers for a given product
   */
  getProductSellers: async (productId: string): Promise<SellerOffer[]> => {
    const endpoint = `/v2/products/${encodeURIComponent(productId)}/offers`;
    const raw = await fetchCatalogResource<unknown>(endpoint);
    if (Array.isArray(raw)) {
      return raw.map(mapOfferToSeller);
    }
    return [];
  },

  /**
   * Get product specifications/details
   */
  getProductSpecifications: async (productId: string): Promise<Record<string, unknown>> => {
    const endpoint = `/v2/products/${encodeURIComponent(productId)}/specifications`;
    const raw = await fetchCatalogResource<unknown>(endpoint);
    return mergeSpecifications(raw);
  },

  /**
   * Fetch catalog search suggestions for typeahead experiences.
   * Backend expects at least a two character query; return empty list if shorter.
   */
  getSearchSuggestions: async (query: string, size: number = 10): Promise<string[]> => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      return [];
    }

    const params = new URLSearchParams({
      q: trimmed,
      size: size.toString()
    });

    return fetchCatalogResource<string[]>(`/v2/search/suggest?${params.toString()}`);
  }
};
